import "./styles.css";
import { useEffect, useState, useRef, useMemo } from "react";

import generalindex from "./jsons/index_general.json";
import index_items from "./jsons/index_items.json";
import index_mobs from "./jsons/index_mobs.json";
import mvps from "./jsons/mvps.json";
import minibosses from "./jsons/minibosses.json";

import EpisodeDetailsView from './components/EpisodeDetailsView.js';

/* Shared, prop-driven pieces reused from the desktop file — layout-independent logic lives there.
   PARITY NOTE: see CLAUDE.md — all desktop content changes must be reflected here (and vice versa). */
import {
  DEFAULT_EPISODE,
  AVAILABLE_EPISODES,
  DROP_SLOTS_ARRAY,
  MVP_DROP_SLOTS_ARRAY,
  getMapUrl,
  DropEntry,
  RateInput,
  ItemMapsTabs,
  SpawnMapsTabs,
  TimelineView,
  WorldMapView,
  ElementDamageTable,
  WpnDmgPerSizeTable,
} from "./App";

/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/
/* DEFAULT EPISODE DATA (same as desktop) */
/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/

const droppedBy_default          = require(`./jsons/${DEFAULT_EPISODE}/dropped_by_${DEFAULT_EPISODE}.json`);
const items_default              = require(`./jsons/${DEFAULT_EPISODE}/item_db_${DEFAULT_EPISODE}.json`);
const item_descriptions_default  = require(`./jsons/${DEFAULT_EPISODE}/item_description_${DEFAULT_EPISODE}.json`);
const mobs_default               = require(`./jsons/${DEFAULT_EPISODE}/mob_db_${DEFAULT_EPISODE}.json`);
const spawn_default              = require(`./jsons/${DEFAULT_EPISODE}/mob_spawn_${DEFAULT_EPISODE}.json`);

/* Mobile always uses the standard theme titles */
const MENU_STYLE_CLASS = "title-left-container";

/* Episode picker options — keep in sync with the desktop <select> list */
const EPISODE_OPTIONS = [
  { value: "default", label: "Test" },
  { value: "00", label: "Ep.00 - Alpha Era" },
  { value: "0", label: "Ep.0 - Beta Era" },
  { value: "1", label: "Ep.1 - Start of an Adventure" },
  { value: "1-5", label: "Ep.1.5 - Attack of the Ancient (Clock Tower + GH dun)" },
  { value: "2", label: "Ep.2 - Lutie" },
  { value: "3", label: "Ep.3 - Comodo" },
  { value: "4", label: "Ep.4 - Turtle Island + WoE" },
  { value: "5", label: "Ep.5 - Juno" },
  { value: "6", label: "Ep.6 - Amatsu" },
  { value: "6-2", label: "Ep.6.2 - Kunlun" },
  { value: "7", label: "Ep.7 - Umbala" },
  { value: "7-1", label: "Ep.7.1 - Nifflheim" },
  { value: "8-1", label: "Ep.8.1 - Louyang" },
  { value: "8-2", label: "Ep.8.2 - Ayothaya" },
  { value: "10-1", label: "Ep.10.1 - Einbroch" },
  { value: "10-2", label: "Ep.10.2 - Lighthalzen" },
  { value: "10-3", label: "Ep.10.3 - Noghaltz" },
  { value: "10-4", label: "Ep.10.4 - Hugel" },
  { value: "11-1", label: "Ep.11.1 - Rachel" },
  { value: "11-2", label: "Ep.11.2 - Veins" },
  { value: "11-3", label: "Ep.11.3 - Nameless Island" },
  { value: "12", label: "Ep.12~13 - Satan Morroc Era" },
  { value: "13-1", label: "Ep.13.1 - Ash Vacuum" },
  { value: "13-2", label: "Ep.13.2 - Into the Unknown" },
];

/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/
/* MOBILE APP */
/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/

export default function AppMobile() {

  // Search + selection
  const [inputBusca, setinputBusca] = useState("");
  const [selectedMob, setselectedMob] = useState(null);
  const [selectedItem, setselectedItem] = useState(null);
  const [oldImageExists, setoldImageExists] = useState(true);
  const [cardImageExists, setcardImageExists] = useState(false);
  const [activeSpawnTab, setactiveSpawnTab] = useState("0");
  const [activeItemMapTab, setactiveItemMapTab] = useState("0");
  const [dropTableSort, setdropTableSort] = useState({ field: "slot", asc: true });
  const [activeResultsTab, setactiveResultsTab] = useState("items"); // mobile-only: Items|Monsters list tabs

  // Global parameters
  const [baseExpMultiplier, setbaseExpMultiplier] = useState("1");
  const [jobExpMultiplier, setjobExpMultiplier] = useState("1");
  const [dropRateMultiplier, setdropRateMultiplier] = useState("1");
  const [currentLanguage, setcurrentLanguage] = useState("en-us");
  const [currentDistribution, setcurrentDistribution] = useState("iRO");
  const [showOptions, setshowOptions] = useState(false); // mobile-only: collapsible options panel

  // Episode data
  const [currentEpisode, setcurrentEpisode] = useState(DEFAULT_EPISODE);
  const [currentEpisodeItemDB, setcurrentEpisodeItemDB] = useState(items_default);
  const [currentEpisodeMobDB, setcurrentEpisodeMobDB] = useState(mobs_default);
  const [currentEpisodeMobSpawn, setcurrentEpisodeMobSpawn] = useState(spawn_default);
  const [currentEpisodeDroppedBy, setcurrentEpisodeDroppedBy] = useState(droppedBy_default);
  const [currentEpisodeItemDescription, setcurrentEpisodeItemDescription] = useState(item_descriptions_default);
  const [selectedEpisodeDetails, setselectedEpisodeDetails] = useState(null);

  // Site pages
  const [selectedPage, setselectedPage] = useState(null);

  // Filters
  const [checkboxState, setcheckboxState] = useState(false);
  const [hideNoSpawn, sethideNoSpawn] = useState(true); /* default ON for the whole site (also drives the Episode Details delta) */
  const [hideNoDrop, sethideNoDrop] = useState(true); /* default ON for the whole site (also drives the Episode Details delta) */

  const persistReadyRef = useRef(false);
  const detailRef = useRef(null);

/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/
/* NAVIGATION HELPERS */
/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/

  const scrollToDetail = () => {
    setTimeout(() => { if (detailRef.current) detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50);
  };

  const goToItem = (itemId) => {
    const item = currentEpisodeItemDB.find((i) => i.ID == itemId);
    if (!item) return;
    setselectedMob(null);
    setselectedEpisodeDetails(null);
    setselectedPage(null);
    setoldImageExists(true);
    setcardImageExists(false);
    setactiveItemMapTab("0");
    setselectedItem(item);
    scrollToDetail();
  };

  const goToMob = (mobId) => {
    const mob = currentEpisodeMobDB.find((m) => m.ID == mobId);
    if (!mob) return;
    setselectedItem(null);
    setselectedEpisodeDetails(null);
    setselectedPage(null);
    setactiveSpawnTab("0");
    setselectedMob(mob);
    scrollToDetail();
  };

  const goToPage = (page) => {
    setselectedItem(null);
    setselectedMob(null);
    setselectedEpisodeDetails(null);
    setselectedPage(page);
  };

/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/
/* EPISODE HANDLING */
/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/

  const handleEpisodeChange = async (episode) => {
    try {
      const newDroppedBy = await import(`./jsons/${episode}/dropped_by_${episode}.json`);
      const newItemDB = await import(`./jsons/${episode}/item_db_${episode}.json`);
      const newItemDescription = await import(`./jsons/${episode}/item_description_${episode}.json`);
      const newMobDB = await import(`./jsons/${episode}/mob_db_${episode}.json`);
      const newMobSpawn = await import(`./jsons/${episode}/mob_spawn_${episode}.json`);
      setcurrentEpisodeDroppedBy(newDroppedBy.default);
      setcurrentEpisodeMobSpawn(newMobSpawn.default);
      setcurrentEpisodeItemDB(newItemDB.default);
      setcurrentEpisodeMobDB(newMobDB.default);
      setcurrentEpisodeItemDescription(newItemDescription.default);
      setcurrentEpisode(episode);
      if (selectedEpisodeDetails !== null) setselectedEpisodeDetails(episode);
      // Re-select current mob/item in the new episode's data, or clear if not found
      if (selectedMob !== null) setselectedMob(newMobDB.default.find((mob) => mob.ID == selectedMob.ID) || null);
      if (selectedItem !== null) setselectedItem(newItemDB.default.find((item) => item.ID == selectedItem.ID) || null);
      setactiveSpawnTab("0");
      setactiveItemMapTab("0");
      setoldImageExists(true);
      setcardImageExists(false);
    } catch (error) {
      alert('Episode not supported yet');
    }
  };

/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/
/* SEARCH - computed inline via useMemo (no useEffect+setState, per project rules) */
/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/

  const filteredItems = useMemo(() => {
    let list;
    if (inputBusca === "" && !checkboxState) list = [];
    else if (checkboxState && inputBusca === "") list = currentEpisodeItemDB;
    else list = currentEpisodeItemDB.filter((item) => {
      const fn = (index_items[item.ID] && index_items[item.ID][currentLanguage]) ? index_items[item.ID][currentLanguage] : (index_items[item.ID] && index_items[item.ID].FriendlyName) || item.FriendlyName || item.ShortName || "";
      return fn.toLowerCase().includes(inputBusca.toLowerCase()) || String(item.ID).includes(inputBusca);
    });
    return hideNoDrop ? list.filter(item => currentEpisodeDroppedBy[item.ID] && currentEpisodeDroppedBy[item.ID].length > 0) : list;
  }, [inputBusca, checkboxState, hideNoDrop, currentEpisodeItemDB, currentEpisodeDroppedBy, currentLanguage]);

  const filteredMobs = useMemo(() => {
    let list;
    if (inputBusca === "" && !checkboxState) list = [];
    else if (checkboxState && inputBusca === "") list = currentEpisodeMobDB;
    else list = currentEpisodeMobDB.filter((mob) => {
      const fn = (index_mobs[mob.ID] && index_mobs[mob.ID][currentLanguage]) ? index_mobs[mob.ID][currentLanguage] : (index_mobs[mob.ID] && index_mobs[mob.ID].AegisName) || mob.AegisName || mob.FriendlyName || "";
      return fn.toLowerCase().includes(inputBusca.toLowerCase()) || String(mob.ID).includes(inputBusca);
    });
    return hideNoSpawn ? list.filter(mob => currentEpisodeMobSpawn.some(s => s.mob_id == mob.ID)) : list;
  }, [inputBusca, checkboxState, hideNoSpawn, currentEpisodeMobDB, currentEpisodeMobSpawn, currentLanguage]);

/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/
/* PERSISTENCE - same localStorage keys as desktop */
/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/

  useEffect(() => { if (persistReadyRef.current) localStorage.setItem('ragdb_episode', currentEpisode); }, [currentEpisode]);
  useEffect(() => { if (persistReadyRef.current) { if (selectedMob) localStorage.setItem('ragdb_mob_id', String(selectedMob.ID)); else localStorage.removeItem('ragdb_mob_id'); } }, [selectedMob]);
  useEffect(() => { if (persistReadyRef.current) { if (selectedItem) localStorage.setItem('ragdb_item_id', String(selectedItem.ID)); else localStorage.removeItem('ragdb_item_id'); } }, [selectedItem]);

  useEffect(() => {
    const restore = async () => {
      try {
        const savedEpisode = localStorage.getItem('ragdb_episode');
        const savedMobId = localStorage.getItem('ragdb_mob_id');
        const savedItemId = localStorage.getItem('ragdb_item_id');
        let itemDB = items_default, mobDB = mobs_default;
        if (savedEpisode && savedEpisode !== DEFAULT_EPISODE) {
          const newDroppedBy = await import(`./jsons/${savedEpisode}/dropped_by_${savedEpisode}.json`);
          const newItemDB = await import(`./jsons/${savedEpisode}/item_db_${savedEpisode}.json`);
          const newItemDescription = await import(`./jsons/${savedEpisode}/item_description_${savedEpisode}.json`);
          const newMobDB = await import(`./jsons/${savedEpisode}/mob_db_${savedEpisode}.json`);
          const newMobSpawn = await import(`./jsons/${savedEpisode}/mob_spawn_${savedEpisode}.json`);
          setcurrentEpisodeDroppedBy(newDroppedBy.default);
          setcurrentEpisodeMobSpawn(newMobSpawn.default);
          setcurrentEpisodeItemDB(newItemDB.default);
          setcurrentEpisodeMobDB(newMobDB.default);
          setcurrentEpisodeItemDescription(newItemDescription.default);
          setcurrentEpisode(savedEpisode);
          itemDB = newItemDB.default; mobDB = newMobDB.default;
        }
        if (savedMobId) { const mob = mobDB.find((m) => m.ID == savedMobId); if (mob) setselectedMob(mob); }
        else if (savedItemId) { const item = itemDB.find((i) => i.ID == savedItemId); if (item) setselectedItem(item); }
      } catch (e) { /* saved episode no longer available - stay on default */ }
      persistReadyRef.current = true;
    };
    restore();
  }, []);

/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/
/* MOBILE APP RETURN */
/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/

  return (
<div className={`global-${currentEpisode}`} style={{ minHeight: "100vh", overflowX: "hidden", fontFamily: "Verdana", fontSize: "13px", paddingBottom: "40px" }}>

{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}
{/* STICKY HEADER - logo + site nav */}
{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}

  <div className={`background-${currentEpisode}`} style={{ padding: "6px 8px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <img src="/img/logo.png" alt="RagnaDB" style={{ maxHeight: "42px" }} />
{/* // Header banner beside the logo (nav moved to its own row to make room) */}
      <div style={{ flex: 1, textAlign: "right", minWidth: 0 }}>
        <img src={`https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/headers/header-${currentEpisode}.gif`} alt="Header Banner" style={{ maxHeight: "42px", maxWidth: "100%" }} onError={(e) => { e.target.style.display = "none"; }} />
      </div>
    </div>
    <div style={{ display: "flex", gap: "6px", overflowX: "auto", paddingTop: "6px" }}>
      <button className={`title-${currentEpisode} nav-menu-button`} style={{ padding: "4px 10px", fontSize: "12px" }} onClick={() => goToPage(null)}>Database</button>
      <button className={`title-${currentEpisode} nav-menu-button`} style={{ padding: "4px 10px", fontSize: "12px" }} onClick={() => goToPage("timeline")}>Timeline</button>
      <button className={`title-${currentEpisode} nav-menu-button`} style={{ padding: "4px 10px", fontSize: "12px" }} onClick={() => goToPage("about")}>About</button>
      <a className={`title-${currentEpisode} nav-menu-button`} style={{ padding: "4px 10px", fontSize: "12px", textDecoration: "none" }} href="https://discord.gg/Cpe6rMN2UR" target="_blank" rel="noopener noreferrer">Discord</a>
    </div>
{/* // Second button row - episode-scoped actions */}
    <div style={{ display: "flex", gap: "6px", overflowX: "auto", paddingTop: "6px" }}>
      <button className={`title-${currentEpisode} nav-menu-button`} style={{ padding: "4px 10px", fontSize: "12px" }} onClick={() => { setselectedItem(null); setselectedMob(null); setselectedPage(null); setselectedEpisodeDetails(currentEpisode); }}>Episode Details</button>
      <button className={`title-${currentEpisode} nav-menu-button`} style={{ padding: "4px 10px", fontSize: "12px" }} onClick={() => goToPage("worldmap")}>World Map</button>
    </div>
  </div>

{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}
{/* DATABASE VIEW */}
{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}

  {selectedPage === null && (
  <>

{/* // Options (collapsible) */}
    <div className={`background-${currentEpisode}`} style={{ margin: "8px", padding: "6px 10px" }}>
      <div className={`title-${currentEpisode}`} style={{ cursor: "pointer", fontWeight: "bold" }} onClick={() => setshowOptions(v => !v)}>{showOptions ? "▾" : "▸"} Options</div>
      {showOptions && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", paddingTop: "8px" }}>
          <div className={`title-${currentEpisode}`}>Base:</div>
          <RateInput style={{ minWidth: "27px", maxWidth: "40px", textAlign: "Center" }} value={baseExpMultiplier} onChange={setbaseExpMultiplier} />
          <div className={`title-${currentEpisode}`}>Job:</div>
          <RateInput style={{ minWidth: "27px", maxWidth: "40px", textAlign: "Center" }} value={jobExpMultiplier} onChange={setjobExpMultiplier} />
          <div className={`title-${currentEpisode}`}>Drop:</div>
          <RateInput style={{ minWidth: "27px", maxWidth: "40px", textAlign: "Center" }} value={dropRateMultiplier} onChange={setdropRateMultiplier} />
          <div className={`title-${currentEpisode}`}>Language:</div>
          <select value={currentLanguage} onChange={(e) => setcurrentLanguage(e.target.value)}>
            <option value="en-us">English</option>
            <option value="pt-br">Português</option>
          </select>
          <div className={`title-${currentEpisode}`}>Distribution:</div>
          <select value={currentDistribution} onChange={(e) => setcurrentDistribution(e.target.value)}>
            <option value="iRO">iRO</option>
            <option value="kRO">kRO</option>
            <option value="bRO">bRO</option>
          </select>
          <label><input type="checkbox" checked={checkboxState} onChange={() => setcheckboxState(v => !v)} /> Show All</label>
          <label><input type="checkbox" checked={hideNoSpawn} onChange={() => sethideNoSpawn(v => !v)} /> Hide Mobs without spawn</label>
          <label><input type="checkbox" checked={hideNoDrop} onChange={() => sethideNoDrop(v => !v)} /> Hide Items that don't drop</label>
        </div>
      )}
    </div>

{/* // Episode picker - above the search bar */}
    <div className={`background-${currentEpisode}`} style={{ margin: "8px", padding: "10px" }}>
      <select value={currentEpisode} onChange={(e) => handleEpisodeChange(e.target.value)} style={{ width: "100%", height: "36px" }}>
        {EPISODE_OPTIONS.map((op) => (
          <option key={op.value} value={op.value} disabled={op.value !== "default" && !AVAILABLE_EPISODES.has(op.value)}>{op.label}</option>
        ))}
      </select>
    </div>

{/* // Search input - sticky: stays glued to the top once scrolled past it */}
    <div className={`background-${currentEpisode}`} style={{ position: "sticky", top: 0, zIndex: 100, margin: "8px", padding: "8px", backdropFilter: "blur(8px)" }}>
      <input
        placeholder="Item/mob name or ID"
        value={inputBusca}
        onChange={(e) => setinputBusca(e.target.value)}
        style={{ width: "100%", height: "36px", boxSizing: "border-box" }}
      />
    </div>

{/* // No results feedback - without this, an already-open detail below looks like the (wrong) search result */}
    {filteredItems.length === 0 && filteredMobs.length === 0 && (inputBusca !== "" || checkboxState) && (
      <div className={`background-${currentEpisode}`} style={{ margin: "8px", padding: "14px 10px", textAlign: "center", opacity: 0.85 }}>
        No results found{inputBusca !== "" ? ` for "${inputBusca}"` : ""} in this episode.
      </div>
    )}

{/* // Results - Items | Monsters tabs */}
    {(filteredItems.length > 0 || filteredMobs.length > 0) && (
    <div className={`background-${currentEpisode}`} style={{ margin: "8px", padding: "10px" }}>
      <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
        <button className={`title-${currentEpisode} nav-menu-button`} style={{ flex: 1, padding: "6px", opacity: activeResultsTab === "items" ? 1 : 0.55 }} onClick={() => setactiveResultsTab("items")}>Items ({filteredItems.length})</button>
        <button className={`title-${currentEpisode} nav-menu-button`} style={{ flex: 1, padding: "6px", opacity: activeResultsTab === "mobs" ? 1 : 0.55 }} onClick={() => setactiveResultsTab("mobs")}>Monsters ({filteredMobs.length})</button>
      </div>
      <div style={{ maxHeight: "45vh", overflowY: "auto" }}>
        {activeResultsTab === "items" && filteredItems.map((item) => (
          <div key={item.ID} className="mobile-row" onClick={() => goToItem(item.ID)}>
            <img src={`https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/icons/${item.ID}.png`} alt="" style={{ width: "24px", height: "24px", objectFit: "contain" }} onError={(e) => { e.target.style.visibility = "hidden"; }} />
            <span>{item.ID} - {index_items[item.ID] ? index_items[item.ID][currentLanguage] : ("* - " + (item.FriendlyName || item.ShortName || item.NAME))} {Number(item.Slots || item.SLOT || item.Slot) > 0 && `[${item.Slots || item.SLOT || item.Slot}]`}</span>
          </div>
        ))}
        {activeResultsTab === "mobs" && filteredMobs.map((mob) => (
          <div key={mob.ID} className="mobile-row" onClick={() => goToMob(mob.ID)}>
            <span>{mob.ID} - {index_mobs[mob.ID] ? index_mobs[mob.ID][currentLanguage] || ("* - " + mob.AegisName) : ("* - " + mob.AegisName)}</span>
            {mvps[mob.ID] && <img src="https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/other/mvp.png" alt="MVP" style={{ height: "14px" }} />}
            {minibosses[mob.ID] && <img src="https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/other/miniboss.png" alt="Mini-boss" style={{ height: "14px" }} />}
          </div>
        ))}
      </div>
    </div>
    )}
  </>
  )}

{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}
{/* ITEM DETAILS */}
{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}

  {selectedPage === null && selectedItem !== null && (
  <div ref={detailRef} className={`background-${currentEpisode}`} style={{ margin: "8px", padding: "10px", textAlign: "left" }}>

{/* // Header */}
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <img src={`https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/icons/${selectedItem.ID}.png`} alt="" style={{ width: "28px", height: "28px", objectFit: "contain" }} />
      <div>
        <div className={`title-${currentEpisode}`} style={{ fontWeight: "bold", fontSize: "17px" }}>
          {index_items[selectedItem.ID] ? index_items[selectedItem.ID][currentLanguage] : (selectedItem.FriendlyName || selectedItem.ShortName || selectedItem.NAME)} {Number(selectedItem.Slots || selectedItem.SLOT || selectedItem.Slot) > 0 && `[${selectedItem.Slots || selectedItem.SLOT || selectedItem.Slot}]`}{/* Slots: must use Number() — do NOT revert to string comparison */}
        </div>
        <div style={{ fontSize: "11px", opacity: 0.75 }}>{selectedItem.ID} | {selectedItem.ShortName || selectedItem.FriendlyName || selectedItem.NAME} | {index_items[selectedItem.ID] ? index_items[selectedItem.ID].AegisName : ""}</div>
      </div>
    </div>

{/* // Description */}
    {currentEpisodeItemDescription[selectedItem.ID]?.description && (
      <div style={{ padding: "8px 0", whiteSpace: "pre-line" }}>{currentEpisodeItemDescription[selectedItem.ID].description}</div>
    )}

{/* // Images */}
    <div style={{ display: "flex", gap: "12px", padding: "6px 0", flexWrap: "wrap" }}>
      <div style={{ textAlign: "center" }}>
        <img src={`https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/images/${selectedItem.ID}.png`} alt="" style={{ maxWidth: "120px" }} />
        <div style={{ fontSize: "10px", opacity: 0.7 }}>New Image</div>
      </div>
      {oldImageExists && (
        <div style={{ textAlign: "center" }}>
          <img src={`https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/images-old/${selectedItem.ID}.png`} alt="" style={{ maxWidth: "120px" }} onError={() => setoldImageExists(false)} />
          <div style={{ fontSize: "10px", opacity: 0.7 }}>Old Image</div>
        </div>
      )}
    </div>

{/* // Details fields (desktop columns 1+2 merged into one stacked list) */}
    <div className={`title-${currentEpisode} ${MENU_STYLE_CLASS}`}>Details</div>
    <div style={{ lineHeight: 1.7 }}>
      {currentEpisodeItemDescription[selectedItem.ID]?.item_class && <div>{generalindex[currentLanguage].general.item_class}: {currentEpisodeItemDescription[selectedItem.ID].item_class}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.compound_on && <div>{generalindex[currentLanguage].general.compound_on || "Compound on"}: {currentEpisodeItemDescription[selectedItem.ID].compound_on}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.defense && <div>{generalindex[currentLanguage].general.defense}: {currentEpisodeItemDescription[selectedItem.ID].defense}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.weight && <div>{generalindex[currentLanguage].general.weight}: {currentEpisodeItemDescription[selectedItem.ID].weight}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.level_required && <div>{generalindex[currentLanguage].general.level_required}: {currentEpisodeItemDescription[selectedItem.ID].level_required}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.applicable_jobs && <div>{generalindex[currentLanguage].general.applicable_jobs}: {currentEpisodeItemDescription[selectedItem.ID].applicable_jobs}</div>}
      <div>{generalindex[currentLanguage].general.npc_price || "Buy At"}: {selectedItem.NPCPrice || selectedItem.Price || selectedItem.PRICE}z</div>
      <div>{generalindex[currentLanguage].general.sell_at || "Sell At"}: {(selectedItem.NPCPrice || selectedItem.Price || selectedItem.PRICE) / 2}z</div>
      {Number(selectedItem.Slots || selectedItem.SLOT || selectedItem.Slot) > 0 && <div>Slots: {selectedItem.Slots || selectedItem.SLOT || selectedItem.Slot}</div>}
      {Number(selectedItem.MinLvl || selectedItem.equipableLevel || selectedItem.level_required || selectedItem.eLV) > 0 && <div>{generalindex[currentLanguage].general.level_required || "Level Required"}: {selectedItem.MinLvl || selectedItem.equipableLevel || selectedItem.level_required || selectedItem.eLV}</div>}
      {Number(selectedItem.ATK || selectedItem.weapon_attack) > 0 && <div>ATK: {selectedItem.ATK || selectedItem.weapon_attack}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.weapon_attack && <div>{generalindex[currentLanguage].general.weapon_attack}: {currentEpisodeItemDescription[selectedItem.ID].weapon_attack}</div>}
      {Number(selectedItem.DEF || selectedItem.defense) > 0 && <div>DEF: {selectedItem.DEF || selectedItem.defense}</div>}
      {Number(selectedItem.MATK) > 0 && <div>MATK: {selectedItem.MATK}</div>}
      {Number(selectedItem.MDEF) > 0 && <div>MDEF: {selectedItem.MDEF}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.weapon_level && <div>{generalindex[currentLanguage].general.weapon_level}: {currentEpisodeItemDescription[selectedItem.ID].weapon_level}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.element && <div>{generalindex[currentLanguage].general.element}: {currentEpisodeItemDescription[selectedItem.ID].element}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.equipped_on && <div>{generalindex[currentLanguage].general.equipped_on}: {currentEpisodeItemDescription[selectedItem.ID].equipped_on}</div>}
      {currentEpisodeItemDescription[selectedItem.ID]?.applicable_pet && <div>Pet: {currentEpisodeItemDescription[selectedItem.ID].applicable_pet}</div>}
      <div>
        {selectedItem.STR > 0 && <span style={{ marginRight: "8px" }}>STR+{selectedItem.STR}</span>}
        {selectedItem.AGI > 0 && <span style={{ marginRight: "8px" }}>AGI+{selectedItem.AGI}</span>}
        {selectedItem.VIT > 0 && <span style={{ marginRight: "8px" }}>VIT+{selectedItem.VIT}</span>}
        {selectedItem.INT > 0 && <span style={{ marginRight: "8px" }}>INT+{selectedItem.INT}</span>}
        {selectedItem.DEX > 0 && <span style={{ marginRight: "8px" }}>DEX+{selectedItem.DEX}</span>}
        {selectedItem.LUK > 0 && <span style={{ marginRight: "8px" }}>LUK+{selectedItem.LUK}</span>}
        {selectedItem.CardHit > 0 && <span style={{ marginRight: "8px" }}>CardHit+{selectedItem.CardHit}</span>}
        {selectedItem.CardCrit > 0 && <span style={{ marginRight: "8px" }}>CardCrit+{selectedItem.CardCrit}</span>}
        {selectedItem.CardFlee > 0 && <span style={{ marginRight: "8px" }}>CardFlee+{selectedItem.CardFlee}</span>}
        {selectedItem.CardSkill > 0 && <span style={{ marginRight: "8px" }}>CardSkill+{selectedItem.CardSkill}</span>}
      </div>
    </div>

{/* // Card Artwork */}
    <div style={{ textAlign: "center", padding: "6px 0", display: cardImageExists ? "block" : "none" }}>
      <div className={`title-${currentEpisode} ${MENU_STYLE_CLASS}`}>Card Artwork</div>
      <img src={`https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/cards/${selectedItem.ID}.png`} alt="" style={{ maxWidth: "100%" }}
        onLoad={() => setcardImageExists(true)} onError={() => setcardImageExists(false)} />
    </div>
    {/* card img must render even when hidden so onLoad can fire; display toggles above */}

{/* // Dropped By */}
    <div className={`title-${currentEpisode} ${MENU_STYLE_CLASS}`}>Dropped By</div>
    {currentEpisodeDroppedBy[selectedItem.ID] ? (
      <div>
        {[...currentEpisodeDroppedBy[selectedItem.ID]]
          .filter(e => e.rate > 0)
          .filter(e => currentEpisodeMobSpawn.some(s => s.mob_id == e.mob_id))
          .sort((a, b) => b.rate - a.rate)
          .filter((e, i, arr) => arr.findIndex(x => x.mob_id === e.mob_id) === i)
          .map((droppedByEntry) => {
            const rate = parseFloat((droppedByEntry.rate / 100 * dropRateMultiplier).toFixed(4));
            return (
              <div key={droppedByEntry.mob_id} className="mobile-row" onClick={() => goToMob(droppedByEntry.mob_id)}>
                <img src={`https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/gifs/${droppedByEntry.mob_id}.gif`} alt="" style={{ maxWidth: "36px", maxHeight: "36px", objectFit: "contain" }} />
                <span style={{ flex: 1 }}>
                  {index_mobs[droppedByEntry.mob_id] ? index_mobs[droppedByEntry.mob_id][currentLanguage] : droppedByEntry.mob_id}
                  {mvps[droppedByEntry.mob_id] && <img src="https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/other/mvp.png" alt="MVP" style={{ height: "13px", marginLeft: "4px" }} />}
                  {minibosses[droppedByEntry.mob_id] && <img src="https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/other/miniboss.png" alt="Mini-boss" style={{ height: "13px", marginLeft: "4px" }} />}
                  <span style={{ opacity: 0.6 }}> - {droppedByEntry.mob_id}</span>
                </span>
                <span>{rate > 100 ? 100 : rate}%</span>
              </div>
            );
          })}
      </div>
    ) : (
      <div>No monsters drop this item.</div>
    )}

{/* // Where to Farm */}
    <ItemMapsTabs
      itemID={selectedItem.ID}
      currentEpisodeDroppedBy={currentEpisodeDroppedBy}
      currentEpisodeMobSpawn={currentEpisodeMobSpawn}
      activeItemMapTab={activeItemMapTab}
      setactiveItemMapTab={setactiveItemMapTab}
      currentEpisode={currentEpisode}
      currentLanguage={currentLanguage}
      onMobClick={goToMob}
      getMapUrl={getMapUrl}
      MENU_STYLE_CLASS={MENU_STYLE_CLASS}
    />
  </div>
  )}

{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}
{/* MOB DETAILS */}
{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}

  {selectedPage === null && selectedMob !== null && (
  <div ref={detailRef} className={`background-${currentEpisode}`} style={{ margin: "8px", padding: "10px", textAlign: "left" }}>

{/* // Header */}
    <div className={`title-${currentEpisode}`} style={{ fontWeight: "bold", fontSize: "17px" }}>
      {index_mobs[selectedMob.ID] ? index_mobs[selectedMob.ID][currentLanguage] : selectedMob.FriendlyName}
      {mvps[selectedMob.ID] && <img src="https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/other/mvp.png" alt="MVP" style={{ height: "16px", marginLeft: "6px" }} />}
      {minibosses[selectedMob.ID] && <img src="https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/other/miniboss.png" alt="Mini-boss" style={{ height: "16px", marginLeft: "6px" }} />}
      {((mvps[selectedMob.ID]?.type === "intance") || (minibosses[selectedMob.ID]?.type === "intance")) && <span style={{ fontSize: "12px", marginLeft: "6px" }}>[Instance]</span>}
    </div>
    <div style={{ fontSize: "11px", opacity: 0.75 }}>{selectedMob.ID} | {selectedMob.FriendlyName} | {index_mobs[selectedMob.ID] ? index_mobs[selectedMob.ID].AegisName : ""}</div>

{/* // Sprite + core stats */}
    <div style={{ display: "flex", gap: "12px", padding: "8px 0", alignItems: "flex-start" }}>
      <div style={{ width: "120px", height: "120px", background: "#e8e8e8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <img src={`https://vitcunha.s3.sa-east-1.amazonaws.com/ragnadb/gifs/${selectedMob.ID}.gif`} alt="" style={{ maxWidth: "120px", maxHeight: "120px" }} />
      </div>
      <div style={{ lineHeight: 1.6, fontSize: "12px" }}>
        <div>HP: {selectedMob.HP}</div>
        <div>{generalindex[currentLanguage].general.base_exp || "Base Exp"}: {selectedMob.EXP * baseExpMultiplier}</div>
        <div>{generalindex[currentLanguage].general.job_exp || "Job Exp"}: {selectedMob.JEXP * jobExpMultiplier}</div>
        <div>{generalindex[currentLanguage].general.size || "Size"}: {generalindex[currentLanguage].size[selectedMob.Size]}</div>
        <div>{generalindex[currentLanguage].general.race || "Race"}: {generalindex[currentLanguage].race[selectedMob.Race]}</div>
        <div>{generalindex[currentLanguage].general.element || "Property"}: {generalindex[currentLanguage].element[selectedMob.Element.charAt(1)]} {selectedMob.Element.charAt(0) / 2}</div>
      </div>
    </div>

{/* // Combat + base stats */}
    <div style={{ display: "flex", gap: "20px", fontSize: "12px", lineHeight: 1.6, flexWrap: "wrap" }}>
      <div>
        <div>Level: {selectedMob.LV}</div>
        <div>MinAtk: {selectedMob.MinAtk}</div>
        <div>MaxAtk: {selectedMob.MaxAtk}</div>
        <div>DEF: {selectedMob.DEF}</div>
      </div>
      <div>
        <div>STR: {selectedMob.STR}</div>
        <div>AGI: {selectedMob.AGI}</div>
        <div>VIT: {selectedMob.VIT}</div>
      </div>
      <div>
        <div>INT: {selectedMob.INT}</div>
        <div>DEX: {selectedMob.DEX}</div>
        <div>LUK: {selectedMob.LUK}</div>
      </div>
    </div>

{/* // Element damage + weapon damage per size (shared components with desktop) */}
    <div style={{ padding: "8px 0" }}>
      <ElementDamageTable selectedMob={selectedMob} currentLanguage={currentLanguage} />
    </div>
    <div className={`title-${currentEpisode} ${MENU_STYLE_CLASS}`}>Weapon Damage by Size</div>
    <WpnDmgPerSizeTable selectedMob={selectedMob} currentEpisode={currentEpisode} />

{/* // Drop Table */}
    <div className={`title-${currentEpisode} ${MENU_STYLE_CLASS}`}>Drop Table</div>
    <div style={{ display: "flex", fontSize: "11px", opacity: 0.8, gap: "10px", padding: "2px 0" }}>
      <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setdropTableSort(s => ({ field: "slot", asc: s.field === "slot" ? !s.asc : true }))}>Sort by Slot {dropTableSort.field === "slot" ? (dropTableSort.asc ? "▲" : "▼") : ""}</span>
      <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setdropTableSort(s => ({ field: "rate", asc: s.field === "rate" ? !s.asc : false }))}>Sort by Rate {dropTableSort.field === "rate" ? (dropTableSort.asc ? "▲" : "▼") : ""}</span>
    </div>
    {(() => {
      const getItemSlots = (itemId) => {
        const item = currentEpisodeItemDB.find(i => i.ID == itemId);
        return item ? (item.Slots || item.SLOT || item.Slot || 0) : 0;
      };
      const regularEntries = [];
      DROP_SLOTS_ARRAY.forEach((dropSlot) => {
        const dropID = selectedMob[`Drop${dropSlot}`];
        const rate = selectedMob[`Rate${dropSlot}`];
        if (dropID > 0 && !(dropID == 512 && rate == 0)) regularEntries.push({ slotNumber: dropSlot, dropID, baseDropRate: rate, isMvp: false, sortSlot: dropSlot });
      });
      const mvpEntries = [];
      MVP_DROP_SLOTS_ARRAY.forEach((mvpSlot) => {
        const dropID = selectedMob[`MvpDrop${mvpSlot}`];
        const rate = selectedMob[`MvpRate${mvpSlot}`];
        if (dropID > 0 && !(dropID == 512 && rate == 0)) mvpEntries.push({ slotNumber: "MVP", dropID, baseDropRate: rate, isMvp: true, sortSlot: 10 + mvpSlot });
      });
      regularEntries.sort((a, b) => {
        const va = dropTableSort.field === "rate" ? a.baseDropRate : a.sortSlot;
        const vb = dropTableSort.field === "rate" ? b.baseDropRate : b.sortSlot;
        return dropTableSort.asc ? va - vb : vb - va;
      });
      return [...regularEntries, ...mvpEntries].map((entry) => (
        <DropEntry
          key={entry.sortSlot}
          slotNumber={entry.slotNumber}
          dropID={entry.dropID}
          dropRateMultiplier={dropRateMultiplier}
          baseDropRate={entry.baseDropRate}
          makeDropClickable={goToItem}
          itemIndex={index_items}
          currentEpisode={currentEpisode}
          currentLanguage={currentLanguage}
          isMvp={entry.isMvp}
          itemSlots={getItemSlots(entry.dropID)}
        />
      ));
    })()}

{/* // Spawn Maps */}
    <div className={`title-${currentEpisode} ${MENU_STYLE_CLASS}`}>Spawn Maps</div>
    <SpawnMapsTabs
      ID={selectedMob.ID}
      currentEpisodeMobSpawn={currentEpisodeMobSpawn}
      activeSpawnTab={activeSpawnTab}
      setactiveSpawnTab={setactiveSpawnTab}
      currentEpisode={currentEpisode}
      currentLanguage={currentLanguage}
      onMobClick={goToMob}
      getMapUrl={getMapUrl}
    />
  </div>
  )}

{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}
{/* EPISODE DETAILS */}
{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}

  {selectedPage === null && selectedEpisodeDetails !== null && (
    <div className={`background-${currentEpisode}`} style={{ margin: "8px", padding: "10px", textAlign: "left" }}>
      <div className={`title-${currentEpisode} ${MENU_STYLE_CLASS}`}>Episode Details</div>
      <EpisodeDetailsView
        currentEpisode={currentEpisode}
        currentLanguage={currentLanguage}
        availableEpisodes={AVAILABLE_EPISODES}
        currentEpisodeMobDB={currentEpisodeMobDB}
        currentEpisodeItemDB={currentEpisodeItemDB}
        currentEpisodeMobSpawn={currentEpisodeMobSpawn}
        currentEpisodeDroppedBy={currentEpisodeDroppedBy}
        hideNoSpawn={hideNoSpawn} sethideNoSpawn={sethideNoSpawn}
        hideNoDrop={hideNoDrop} sethideNoDrop={sethideNoDrop}
        onMobClick={goToMob}
        onItemClick={goToItem}
      />
    </div>
  )}

{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}
{/* SITE PAGES - ABOUT / TIMELINE */}
{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}

  {selectedPage === "about" && (
    <div className={`background-${currentEpisode}`} style={{ margin: "8px", padding: "10px", textAlign: "left" }}>
      <div className={`title-${currentEpisode} ${MENU_STYLE_CLASS}`}>About the project</div>
      <p>RagnaDB is a time-travelling database for classic Ragnarok Online — browse items, monsters, drops and spawns exactly as they were in each episode.</p>
    </div>
  )}

  {selectedPage === "timeline" && (
    <div style={{ margin: "8px", height: "calc(100vh - 110px)", display: "flex", flexDirection: "column" }}>
      <TimelineView />
    </div>
  )}

{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}
{/* WORLD MAP */}
{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}

  {selectedPage === "worldmap" && (
    <div style={{ margin: "8px", height: "calc(100vh - 130px)", display: "flex", flexDirection: "column" }}>
      <WorldMapView
        currentEpisode={currentEpisode}
        currentLanguage={currentLanguage}
        currentEpisodeMobSpawn={currentEpisodeMobSpawn}
        getMapUrl={getMapUrl}
        onMobClick={goToMob}
      />
    </div>
  )}

{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}
{/* FOOTER */}
{/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/}

  <div style={{ textAlign: "center", fontSize: "10px", color: "white", fontFamily: "OCRAStd", padding: "14px 0 4px 0" }}>
    <span>Made with <span style={{ color: "#e25555", fontSize: "13px" }}>❤</span> by vitcunha & oldinroplayer</span><br />
    <a href="https://discord.gg/Cpe6rMN2UR" target="_blank" rel="noopener noreferrer" style={{ color: "white", textDecoration: "underline" }}>Join our Discord</a> | <a href="https://ko-fi.com/vitcunha" target="_blank" rel="noopener noreferrer" style={{ color: "white", textDecoration: "underline" }}>Buy us a coffee</a>
  </div>

</div>
  );
}
